import styled from 'threads5/styles/styled';
import Text from 'threads5/Text';
import { animated, useSpring } from '@react-spring/web';
import { string } from 'yup';

import useIsPotato from 'src/hooks/useIsPotato';
import { Box, Stack } from 'src/components-v2/Layout';
import { TextInput } from 'src/components/Inputs';
import { getRequiredMessage } from 'src/lib/utils';
import EmployeeCount from 'src/components/Inputs/EmployeeCount/EmployeeCount';
import TextArea from 'src/components/Inputs/TextArea/TextArea';

export const Heading = styled('h3')(({ theme }) => {
  return {
    color: '#02294B',
    fontFamily: theme.typography.plaidSans,
    fontSize: '2.0rem',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    letterSpacing: '-0.6px',
    margin: 0,
    [theme.breakpoints.up('sm')]: {
      fontSize: '2.4rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '3.0rem',
    },
  };
});

export const Disclaimer = styled(Text)(({ theme }) => {
  return {
    color: '#24555B',
    fontFamily: theme.typography.sans,
    fontSize: '1.4rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '133%',
    opacity: 0.58,
  };
});

export const Border = styled(animated.img)({
  position: 'absolute',
  maxWidth: 'none',
  minWidth: '100%',
  minHeight: '100%',
  aspectRatio: 1,
  scale: 1.5,
  zIndex: -1,
  left: '50%',
  top: '50%',
  translate: '-50% -50%',
});

export const CardBox = styled('div')(({ theme }) => {
  return {
    padding: '2px',
    borderRadius: '18px',
    zIndex: 1,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0px 0px 36px 5px rgba(243, 49, 247, 0.43)',
    [theme.breakpoints.up('md')]: {
      padding: '5px',
      borderRadius: '40px',
    },
  };
});

export const CardBoxInner = styled('div')(({ theme }) => {
  return {
    background: 'white',
    width: '100%',
    height: '100%',
    borderRadius: '17px',
    padding: '37px 32px 50px',
    [theme.breakpoints.up('sm')]: {
      padding: '60px 63px',
    },
    [theme.breakpoints.up('md')]: {
      borderRadius: '35px',
      padding: '70px',
    },
  };
});

export const Card = ({ children }: { children: React.ReactNode }) => {
  const isPotato = useIsPotato();
  const [spring, api] = useSpring(() => {
    return {
      rotateZ: 0,
      config: { duration: 7500 },
    };
  });

  const handleMouseEnter = () => {
    if (isPotato) {
      return;
    }
    api.start({
      loop: { reverse: false },
      to: async (next) => {
        while (true) {
          await next({ rotateZ: 360 });
          api.set({ rotateZ: 0 });
        }
      },
    });
  };

  const handleMouseLeave = () => {
    api.stop();
  };

  return (
    <Box
      className='cms-contact-form-card hide-for-aeo'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ position: 'relative' }}
    >
      <CardBox className='cms-contact-form-card-box'>
        <CardBoxInner className='cms-contact-form-card-box-inner'>
          {children}
        </CardBoxInner>
        <Border
          className='card-border'
          role='presentation'
          src='https://images.ctfassets.net/ss5kfr270og3/2QX6nduCZaLnMJmP8D2VBR/ecd2946f6c9d506a727f9cd1d0544984/AdobeStock_566188450__3_.png?fm=webp&q=1&w=500'
          style={{
            transform: spring.rotateZ.to((r: number) => {
              return `rotate(${r}deg)`;
            }),
          }}
        />
      </CardBox>
    </Box>
  );
};

interface IFormContainerProps {
  errors?: {
    Country?: {
      message: string;
    };
  };
}

export const FormContainer = styled('div')<IFormContainerProps>(() => {
  return {
    'input[type="text"], input[type="number"]': {
      borderRadius: '8px',
      borderColor: '#02294B',
      marginBottom: '8px',
    },
    'fieldset[class^=BaseInput-module_container]': {
      marginBottom: '32px',
    },
    '.customSelect__control': {
      borderRadius: '8px',
      borderColor: '#02294B',
    },
    'span[class^=BaseInput-module_note]': {
      fontSize: '1.4rem',
    },
    '.customSelect__menu': {
      height: '185px !important',
      '& > div': {
        height: '184px !important',
      },
    },
  };
});

export const StyledBackgroundImage = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '& .lazyload-wrapper': {
    width: '100%',
    height: '100%',
  },
});

export const CopyStack = styled(Stack)(({ theme }) => {
  return {
    gap: theme.spacing(2.5),
  };
});

export const ContentStack = styled(Stack)(({ theme }) => {
  return {
    justifyContent: 'center',
    gap: theme.spacing(2.5),
    zIndex: 1,
    position: 'relative',
    height: 'auto',
    [theme.breakpoints.up('md')]: {
      height: '100%',
      // The spacer below the copy is hidden once the columns sit side by
      // side, so this gap would otherwise remain under the text.
      gap: 0,
    },
    // Remove the trailing paragraph Contentful's rich text editor adds.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '& p:nth-last-of-type(1):empty': {
      display: 'none',
    },
  };
});

export const FormFieldStack = styled(Stack)(({ theme }) => {
  return {
    flexDirection: 'column',
    gap: 0,
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      gap: theme.spacing(2),
    },
  };
});

export const MakeTextField = ({
  contactFormData,
  field,
  errors,
  register,
  pattern = {},
  value = '',
  required = true,
  type = 'text',
}) => {
  return (
    <TextInput
      className='cms-contact-form-text-input'
      label={contactFormData[field].label}
      id={contactFormData[field].name}
      name={contactFormData[field].name}
      describedby={contactFormData[field].describedby}
      errorMessage={errors?.[contactFormData[field].name]?.message}
      register={register}
      {...(required && {
        required: getRequiredMessage(contactFormData[field].label),
      })}
      autoComplete={contactFormData[field].autoComplete}
      {...(pattern && { pattern })}
      {...(value && { value })}
      type={type}
    />
  );
};

// `FormContainer` only styles `input[type="text"]` / `input[type="number"]`, so
// a raw textarea inherits none of the card's input treatment. Height and
// `resize` come from TextArea.module.scss; a descendant selector outranks that
// single class, so only the untreated properties are set here.
export const StyledCommentsField = styled('div')({
  '& textarea': {
    width: '100%',
    border: '1px solid #02294B',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '1.6rem',
    // 22px of margin leaves 26px of white space between this border
    // and the disclaimer glyphs. The disclaimer line box starts higher
    // than the letters.
    marginBottom: '22px',
  },
});

export const CommentsField = ({
  contactFormData,
  errors,
  register,
  placeholder,
  inspectorProps = {},
  followedByIntent = false,
}) => {
  return (
    <StyledCommentsField
      className='cms-contact-form-comments'
      {...inspectorProps}
      sx={
        followedByIntent
          ? {
              // The intent legend starts at the top of its line, so it does
              // not need the 4px disclaimer glyph offset. 16px matches the
              // gap the intent heading uses under the phone row.
              '& textarea': { marginBottom: '16px' },
            }
          : undefined
      }
    >
      <TextArea
        {...contactFormData.comments}
        placeholder={placeholder || contactFormData.comments.placeholder}
        errorMessage={errors?.[contactFormData.comments.name]?.message}
        register={register}
      />
    </StyledCommentsField>
  );
};

export const CompanyFields = ({
  contactFormData,
  control,
  errors,
  locale,
  register,
  setValue,
}) => {
  const { marketoKey } = contactFormData;

  return (
    <FormFieldStack className='cms-contact-form-stack-company-email'>
      {MakeTextField({
        contactFormData,
        field: 'company',
        errors,
        register,
      })}
      {MakeTextField({
        contactFormData,
        field: 'email',
        errors,
        register,
        pattern: {
          value: string().email().tests[0].OPTIONS.params.regex,
          message: contactFormData.email.invalidMessage,
        },
        value: '',
      })}
      <EmployeeCount
        control={control}
        errors={errors}
        locale={locale}
        marketoKey={marketoKey}
        register={register}
        setValue={setValue}
      />
    </FormFieldStack>
  );
};
